import type { OnInit } from '@angular/core';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import type { Image } from '@core-mkt/interfaces/image';
import { BrandConfigurationService } from '@core-mkt/services/configuration/brand-configuration.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { ProductService } from '@core-mkt/services/product/product.service';
import { ensureTrailingSlash, getParsedLinkString, getQueryParamString } from '@core-mkt/services/query-param/query-helpers';
import { QueryParamService } from '@core-mkt/services/query-param/query-param.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '@core-mkt/shared/components/vision-button/vision-button/vision-button.component';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { ResourceCard } from '../resources/resources';

@Component({
  selector: 'resource-card',
  templateUrl: './resource-card.component.html',
  styleUrls: ['./resource-card.component.scss'],
  standalone: true,
  imports: [CommonModule, VisionButtonComponent, LazyImgDirective],
})
export class ResourceCardComponent implements OnInit {
  @Input() data: ResourceCard;
  @Input() cardLocation: string;
  faAngleRight = faAngleRight;
  faAngleRightStyles = { width: '16px', height: '32px' };
  resourceTextContent = undefined;
  isDarkBackgroundColor: boolean;

  constructor(
    private redactor: WysiwygRedactorService,
    private imgixService: ImgixService,
    private themeService: ThemeParserService,
    private ps: ProductService,
    private queryParamService: QueryParamService,
    private brand: BrandConfigurationService,
    private env: EnvService,
  ) {}

  get image(): Image | undefined {
    return this.data.resourceImage?.[0];
  }

  get backgroundColor(): string {
    if (!this.data.resourceBackgroundColor) {
      return 'bg-white text-vis-regular';
    }
    if (typeof this.data.resourceBackgroundColor === 'string') {
      return this.data.resourceBackgroundColor;
    }
    return this.themeService.getDominantClassFromStyleList(this.data.resourceBackgroundColor);
  }

  get textContent(): string {
    return this.resourceTextContent;
  }

  get ctaStyle(): string {
    return this.data.resourceCtaStyle;
  }

  get ctaLabel(): string {
    return this.data.resourceCtaLabel ? this.data.resourceCtaLabel : 'Read More';
  }

  get ctaLink(): string {
    if (!this.data.resourceCtaLink) {
      return this.data.resourceCtaLink;
    }
    return this.env.get.enforceTrailingSlash
      ? ensureTrailingSlash(this.data.resourceCtaLink)
      : this.data.resourceCtaLink;
  }

  get ctaTag(): string {
    return this.data.resourceCtaTag;
  }

  get ctaNewTab(): boolean {
    return this.data.resourceCtaLinkNewTab;
  }

  get ctaNofollow(): boolean {
    return this.data.resourceCtaLinkNofollow;
  }

  get buttonData(): VisionButton {
    return {
      bgTheme: this.backgroundColor,
      buttonLink: this.ctaLink,
      buttonAriaLabel: this.data.resourceCtaLinkAriaLabel,
      buttonText: this.ctaLabel,
      buttonStyle: this.ctaStyle,
      useIcon: true,
      newTab: this.data.resourceCtaLinkNewTab,
      nofollow: this.data.resourceCtaLinkNofollow,
      noopener: this.data.resourceCtaLinkNewTab,
      noreferrer: this.data.resourceCtaLinkNewTab,
      ctaTag: this.data.resourceCtaTag,
      fullWidth: false,
    };
  }

  ngOnInit(): void {
    this.resourceTextContent = this.redactor.bypassSanitizer(this.data.resourceTextContent);

    //Getting Aria label to Cta Link
    if (this.data.resourceCtaLink) {
      this.getAriaLabelToCheckout(this.data.resourceCtaLink);
    }

    this.isDarkBackgroundColor = Array.isArray(this.data.resourceBackgroundColor) && this.data.resourceBackgroundColor.includes('bg-vis-dark');
  }

  getTargetUrl(): string | undefined {
    const link = this.ctaLink;
    if (!link) {
      return undefined;
    }
    const queryParams = this.queryParamService.getQueryParams();
    const blogPrefix = this.env.get.blogPrefix;
    const queryString = getQueryParamString(queryParams, link, blogPrefix);
    return getParsedLinkString(link, queryString, this.brand.hostName, this.brand.siblingSites, blogPrefix);
  }

  ctaClick(): void {
    const target = this.getTargetUrl();
    if (target) {
      window.location.href = target;
    }
  }

  /**
   * Getting aria label from product title
   * valitating to get aria label according url link
   */
  getAriaLabelToCheckout(link: string): void {
    if (link?.startsWith('https://checkout')) {
      const linkList = link?.split('?');
      const queryString = linkList[linkList.length - 1];
      const urlParams = new URLSearchParams(queryString);
      const productId = urlParams.get('productId');
      const productEntry: CraftXGritProductEntry = {
        xgritProductId: productId,
      };

      this.ps.getProductData(productEntry).then((product: XGritCompleteProduct) => {
        this.data.resourceCtaLinkAriaLabel = product.apiData.title ? product.apiData.title : undefined;
      });
    }
  }
}
